// src/pages/caisse/OperationsCaisse.jsx
import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, Button, Pagination, 
    Modal, Badge, Spinner, Tooltip, OverlayTrigger
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { API_BASE_URL } from "../../constants/api";
import { COLORS } from "../../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   PAGINATION
   ============================================================ */
const TablePagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    return (
        <Pagination className="pagination-charte mb-0">
            <Pagination.Prev onClick={() => onPageChange(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (currentPage <= 3) pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = currentPage - 2 + i;
                return (
                    <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => onPageChange(pageNum)}>
                        {pageNum}
                    </Pagination.Item>
                );
            })}
            <Pagination.Next onClick={() => onPageChange(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
        </Pagination>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function OperationsCaisse() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortField, setSortField] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [selectedOperations, setSelectedOperations] = useState([]);
    const [exportLoading, setExportLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [operationToDelete, setOperationToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });
    const [expandedRows, setExpandedRows] = useState({});
    const [stats, setStats] = useState(null);
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        fetchOperations();
        fetchStats();
    }, []);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchOperations = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/caisse.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    navigate("/pages/signin2");
                    throw new Error("Session expirée");
                }
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            const result = await response.json();
            setData(result.data || []);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/caisse.php?action=stats`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement stats", err);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleDeleteClick = (operation) => {
        setOperationToDelete(operation);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!operationToDelete) return;
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/caisse.php?id=${operationToDelete.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setShowDeleteModal(false);
                setOperationToDelete(null);
                fetchOperations();
                fetchStats();
                showToast('success', 'Succès', "Opération supprimée avec succès");
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la suppression");
                setShowDeleteModal(false);
            }
        } catch (err) {
            showToast('error', 'Erreur', "Erreur de connexion au serveur");
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    const toggleExpandRow = (id) => {
        setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
    };

    /* ============================================================
       DATA PROCESSING
       ============================================================ */
    const mainOperations = useMemo(() => {
        return data.filter(op => op.type === 'initial' || !op.parent_id);
    }, [data]);

    const enrichedOperations = useMemo(() => {
        return mainOperations.map(op => ({
            ...op,
            montantTotal: op.montant_total || op.montant_initial || op.montant || 0,
            avanceInitiale: op.avance_initiale || op.avance || 0,
            totalVersé: op.total_verse || op.avance || 0,
            resteReel: op.reste_reel || op.reste || 0,
            sommePaiements: op.somme_paiements || 0,
            nbPaiements: op.nb_paiements || (op.paiements?.length || 0),
            pourcentagePaye: op.pourcentage_paye || 0
        }));
    }, [mainOperations]);

    const getPaiementsWithCalcul = (parentOperation) => {
        const paiements = data.filter(op => op.parent_id === parentOperation.id);
        const sortedPaiements = [...paiements].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        const montantTotal = parentOperation.montant_initial || parentOperation.montant || 0;
        const avanceInitiale = parentOperation.avance || 0;
        const paiementsAvecReste = [];
        let montantDuAvant = montantTotal - avanceInitiale;

        for (let i = 0; i < sortedPaiements.length; i++) {
            const paiement = sortedPaiements[i];
            const montantPaye = Number(paiement.avance) || 0;
            const nouveauReste = Math.max(0, montantDuAvant - montantPaye);
            paiementsAvecReste.push({
                ...paiement,
                montant_du_avant: montantDuAvant,
                montant_paye: montantPaye,
                reste_apres: nouveauReste
            });
            montantDuAvant = nouveauReste;
        }
        return paiementsAvecReste;
    };

    const filteredAndSortedItems = useMemo(() => {
        let filtered = enrichedOperations.filter(item => {
            const textMatch =
                (item.beneficiaire_nom || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.beneficiaire_prenom || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.numero_bordereau || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.motif || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.createur_nom || '').toLowerCase().includes(filterText.toLowerCase());

            let dateMatch = true;
            const itemDate = new Date(item.created_at).toISOString().split('T')[0];
            if (dateDebut && dateFin) dateMatch = itemDate >= dateDebut && itemDate <= dateFin;
            else if (dateDebut) dateMatch = itemDate >= dateDebut;
            else if (dateFin) dateMatch = itemDate <= dateFin;

            return textMatch && dateMatch;
        });

        filtered.sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];
            if (sortField === 'montantTotal') { aValue = a.montantTotal || 0; bValue = b.montantTotal || 0; }
            else if (sortField === 'resteReel') { aValue = a.resteReel || 0; bValue = b.resteReel || 0; }
            return sortDirection === 'asc' ? (aValue > bValue ? 1 : -1) : (aValue < bValue ? 1 : -1);
        });

        return filtered;
    }, [filterText, sortField, sortDirection, enrichedOperations, dateDebut, dateFin]);

    const dynamicStats = useMemo(() => {
        const total = filteredAndSortedItems.length;
        const soldes = filteredAndSortedItems.filter(op => op.resteReel <= 0).length;
        const encours = filteredAndSortedItems.filter(op => op.resteReel > 0 && op.totalVersé > 0).length;
        const impayes = filteredAndSortedItems.filter(op => op.resteReel > 0 && op.totalVersé <= 0).length;
        return { total, soldes, encours, impayes };
    }, [filteredAndSortedItems]);

    const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

    const handleSort = (field) => {
        if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDirection('asc'); }
    };

    const resetFilters = () => {
        setFilterText('');
        setDateDebut('');
        setDateFin('');
        setCurrentPage(1);
    };

    /* ============================================================
       EXPORTS
       ============================================================ */
    const handleExport = (format) => {
        setExportLoading(true);
        const dataToExport = selectedOperations.length > 0
            ? enrichedOperations.filter(op => selectedOperations.includes(op.id))
            : filteredAndSortedItems;

        if (!dataToExport.length) {
            showToast('warning', 'Attention', "Aucune donnée à exporter");
            setExportLoading(false);
            return;
        }

        if (format === "csv") {
            const headers = ["Bordereau","Bénéficiaire","Montant","Motif","Total versé","Reste","Date","Statut"];
            const csvContent = [
                "\uFEFF" + headers.join(";"),
                ...dataToExport.map(op => {
                    const statut = op.resteReel <= 0 ? 'Soldé' : (op.totalVersé > 0 ? 'Avance partielle' : 'Impayé');
                    return [
                        op.numero_bordereau,
                        `${op.beneficiaire_prenom} ${op.beneficiaire_nom}`,
                        op.montantTotal, op.motif, op.totalVersé, op.resteReel,
                        formatDate(op.created_at), statut
                    ].map(v => `"${v}"`).join(";");
                })
            ].join("\n");
            downloadFile(csvContent, "text/csv;charset=utf-8;", `export-caisse-${Date.now()}.csv`);
            showToast('success', 'Export CSV', `${dataToExport.length} opération(s) exportée(s)`);
        } else if (format === "excel") {
            const ws = XLSX.utils.json_to_sheet(dataToExport.map(op => {
                const statut = op.resteReel <= 0 ? 'Soldé' : (op.totalVersé > 0 ? 'Avance partielle' : 'Impayé');
                return {
                    Bordereau: op.numero_bordereau,
                    Bénéficiaire: `${op.beneficiaire_prenom} ${op.beneficiaire_nom}`,
                    Montant: op.montantTotal,
                    Motif: op.motif,
                    "Avance initiale": op.avanceInitiale,
                    "Total versé": op.totalVersé,
                    Reste: op.resteReel,
                    Date: formatDate(op.created_at),
                    Statut: statut,
                    "Nb paiements": op.nbPaiements
                };
            }));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Operations");
            XLSX.writeFile(wb, `export-caisse-${Date.now()}.xlsx`);
            showToast('success', 'Export Excel', `${dataToExport.length} opération(s) exportée(s)`);
        } else if (format === "pdf") {
            generatePDF(dataToExport);
            showToast('success', 'Export PDF', `${dataToExport.length} opération(s) exportée(s)`);
        }
        setExportLoading(false);
    };

    const generatePDF = (data) => {
        const doc = new jsPDF();
        doc.setFillColor(41, 90, 167);
        doc.rect(0, 0, 210, 25, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text("HISTORIQUE DES DÉCAISSEMENTS", 105, 15, { align: 'center' });
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text(`Export généré le: ${new Date().toLocaleDateString('fr-FR')}`, 15, 22);
        doc.text(`Total: ${data.length} opération(s)`, 180, 22, { align: 'right' });

        const columns = ["BORDEREAU", "BÉNÉFICIAIRE", "MONTANT", "VERSÉ", "RESTE", "DATE", "STATUT"];
        const rows = data.map(item => {
            const statut = item.resteReel <= 0 ? 'Soldé' : (item.totalVersé > 0 ? 'Avance partielle' : 'Impayé');
            return [
                item.numero_bordereau,
                `${item.beneficiaire_prenom} ${item.beneficiaire_nom}`,
                new Intl.NumberFormat('fr-FR').format(item.montantTotal) + ' FCFA',
                new Intl.NumberFormat('fr-FR').format(item.totalVersé) + ' FCFA',
                new Intl.NumberFormat('fr-FR').format(item.resteReel) + ' FCFA',
                formatDate(item.created_at),
                statut
            ];
        });

        doc.autoTable({
            head: [columns],
            body: rows,
            startY: 35,
            theme: 'striped',
            headStyles: { fillColor: [41, 90, 167], textColor: 255, fontSize: 8 },
            bodyStyles: { fontSize: 7 },
            columnStyles: {
                0: { cellWidth: 25 }, 1: { cellWidth: 35 },
                2: { cellWidth: 20, halign: 'right' }, 3: { cellWidth: 20, halign: 'right' },
                4: { cellWidth: 20, halign: 'right' }, 5: { cellWidth: 20 }, 6: { cellWidth: 20 }
            },
            margin: { left: 15, right: 15 }
        });
        doc.save(`caisse-${Date.now()}.pdf`);
    };

    const downloadFile = (content, type, filename) => {
        const blob = new Blob([content], { type });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 100);
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const SortableHeader = ({ field, children, className = '' }) => (
        <th className={`sortable-th-charte ${className}`} onClick={() => handleSort(field)}>
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortField === field && (
                    <i className={`ri-arrow-${sortDirection === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const getStatutConfig = (reste, totalVerse) => {
        if (reste <= 0) return { gradient: 'linear-gradient(135deg, #10b981, #059669)', label: 'Soldé', icon: 'ri-checkbox-circle-line' };
        if (totalVerse > 0) return { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', label: 'En cours', icon: 'ri-time-line' };
        return { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', label: 'Impayé', icon: 'ri-error-warning-line' };
    };

    /* ============================================================
       LOADING
       ============================================================ */
    if (loading && !mainOperations.length) {
    
               return (
                   <>
                       <Header />
                       <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                           <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                               <div className="text-center" style={{ maxWidth: '400px' }}>
                                   <div className="loading-ring-wrapper mb-4">
                                       <div className="loading-ring"></div>
                                       <div className="loading-ring-inner"></div>
                                       <i className="ri-loader-4-fill loading-icon"></i>
                                   </div>
                                   <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                       Chargement de la page...
                                   </h5>
                                   <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                                   <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                       <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                           <div
                                               className="progress-bar progress-bar-striped progress-bar-animated"
                                               style={{
                                                   width: '100%',
                                                   background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                                   backgroundSize: '200% 100%',
                                                   animation: 'shimmer 1.5s linear infinite'
                                               }}
                                           />
                                       </div>
                                   </div>
                               </div>
                           </div>
                       </div>
                       <Footer />
                       <style>{`
                           .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                           .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                           .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                           .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                           @keyframes spin { to { transform: rotate(360deg); } }
                           @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                           @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                       `}</style>
                   </>
               );
           }
    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-bank-card-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Historique des décaissements</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-bar-chart-line"></i> Total {dynamicStats.total}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-checkbox-circle-line"></i> Soldés {dynamicStats.soldes}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                        <i className="ri-time-line"></i> En cours {dynamicStats.encours}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                        <i className="ri-error-warning-line"></i> Impayés {dynamicStats.impayes}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchOperations} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-primary" as={Link} to="/caisse/decaissement">
                                <i className="ri-add-line"></i> Nouveau décaissement
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-primary">
                                    <div className="kpi-icon-charte"><i className="ri-bank-card-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Total décaissem.</span>
                                        <h3 className="kpi-value-charte">{formatCurrency(stats.totaux?.total_decaissements || 0)}</h3>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-danger">
                                    <div className="kpi-icon-charte"><i className="ri-alert-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Reste à payer</span>
                                        <h3 className="kpi-value-charte text-danger">{formatCurrency(stats.totaux?.reste_total || 0)}</h3>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-success">
                                    <div className="kpi-icon-charte"><i className="ri-calendar-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Aujourd'hui</span>
                                        <h3 className="kpi-value-charte">{formatCurrency(stats.aujourdhui?.montant_jour || 0)}</h3>
                                        <span className="kpi-unit-charte">{stats.aujourdhui?.nb_operations_jour || 0} opération(s)</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-info">
                                    <div className="kpi-icon-charte"><i className="ri-bar-chart-2-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Mois en cours</span>
                                        <h3 className="kpi-value-charte">{formatCurrency(stats.mois_courant?.montant_mois || 0)}</h3>
                                        <span className="kpi-unit-charte">{stats.mois_courant?.nb_operations_mois || 0} opération(s)</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="4">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher par bénéficiaire, bordereau..."
                                            value={filterText}
                                            onChange={e => setFilterText(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="2">
                                    <Form.Control
                                        type="date"
                                        value={dateDebut}
                                        onChange={e => setDateDebut(e.target.value)}
                                        className="filter-select-charte"
                                    />
                                </Col>
                                <Col md="2">
                                    <Form.Control
                                        type="date"
                                        value={dateFin}
                                        onChange={e => setDateFin(e.target.value)}
                                        className="filter-select-charte"
                                    />
                                </Col>
                                <Col md="2">
                                    <div className="d-flex gap-2">
                                        <Form.Select
                                            value={itemsPerPage}
                                            onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                            className="filter-select-charte"
                                        >
                                            <option value={5}>5</option>
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                        </Form.Select>
                                        {(filterText || dateDebut || dateFin) && (
                                            <Button
                                                onClick={resetFilters}
                                                className="btn-modern-light-outline"
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>
                                        )}
                                    </div>
                                </Col>
                                <Col md="2" className="text-end">
                                    <span className="count-badge-charte">
                                        <i className="ri-stack-line"></i>
                                        {filteredAndSortedItems.length} décaissement(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-history-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Historique des décaissements</span>
                                    <span className="badge-header-light">{filteredAndSortedItems.length}</span>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button className="btn-header-light" onClick={() => handleExport('csv')} disabled={exportLoading}>
                                        <i className="ri-file-text-line"></i> CSV
                                    </Button>
                                    <Button className="btn-header-light" onClick={() => handleExport('excel')} disabled={exportLoading}>
                                        <i className="ri-file-excel-line"></i> Excel
                                    </Button>
                                    <Button className="btn-header-light" onClick={() => handleExport('pdf')} disabled={exportLoading}>
                                        <i className="ri-file-pdf-line"></i> PDF
                                    </Button>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Référence</th>
                                            <SortableHeader field="beneficiaire_nom">Bénéficiaire</SortableHeader>
                                            <SortableHeader field="montantTotal" className="text-end">Mont. total</SortableHeader>
                                            <th className="text-end">Avance init.</th>
                                            <th className="text-end">Total versé</th>
                                            <SortableHeader field="resteReel" className="text-end">Reste</SortableHeader>
                                            <SortableHeader field="created_at">Date</SortableHeader>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? currentItems.map((operation) => {
                                            const paiementsAvecCalcul = getPaiementsWithCalcul(operation);
                                            const isExpanded = expandedRows[operation.id];
                                            const statut = getStatutConfig(operation.resteReel, operation.totalVersé);
                                            return (
                                                <React.Fragment key={operation.id}>
                                                    <tr className={isExpanded ? 'expanded-row-charte' : ''}>
                                                        <td className="ps-4">
                                                            <span
                                                                onClick={() => toggleExpandRow(operation.id)}
                                                                className="reference-badge-charte clickable"
                                                            >
                                                                <i className="ri-arrow-right-s-line expand-icon"></i>
                                                                {operation.numero_bordereau}
                                                            </span>
                                                        </td>
                                                        <td style={{ minWidth: '220px' }}>
                                                            <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                                                                {operation.beneficiaire_prenom} {operation.beneficiaire_nom}
                                                            </div>
                                                            <small className="text-muted">
                                                                {operation.motif?.substring(0, 40)}{operation.motif?.length > 40 ? '...' : ''}
                                                            </small>
                                                        </td>
                                                        <td className="text-end">
                                                            <span className="fw-bold" style={{ color: COLORS.primaryDark }}>
                                                                {formatCurrency(operation.montantTotal)}
                                                            </span>
                                                        </td>
                                                        <td className="text-end text-muted">
                                                            {formatCurrency(operation.avanceInitiale)}
                                                        </td>
                                                        <td className="text-end">
                                                            <span className="fw-semibold" style={{ color: COLORS.success }}>
                                                                {formatCurrency(operation.totalVersé)}
                                                            </span>
                                                            {operation.nbPaiements > 0 && (
                                                                <small className="text-muted d-block">{operation.nbPaiements} paiement(s)</small>
                                                            )}
                                                        </td>
                                                        <td className="text-end">
                                                            <span className={`status-badge-charte ${operation.resteReel <= 0 ? 'status-solde' : 'status-reste'}`}>
                                                                <i className={operation.resteReel <= 0 ? 'ri-check-line' : 'ri-error-warning-line'}></i>
                                                                {formatCurrency(operation.resteReel)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="small text-muted">{formatDate(operation.created_at)}</div>
                                                            <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                                {new Date(operation.created_at).toLocaleTimeString('fr-FR')}
                                                            </small>
                                                        </td>
                                                        <td className="text-center pe-4">
                                                            <div className="d-flex gap-2 justify-content-center">
                                                                <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                                                                    <button
                                                                        className="action-btn-charte"
                                                                        style={{ '--btn-color': COLORS.primary }}
                                                                        onClick={() => navigate(`/caisse/operations/${operation.id}`)}
                                                                    >
                                                                        <i className="ri-eye-line"></i>
                                                                    </button>
                                                                </OverlayTrigger>
                                                                <OverlayTrigger placement="top" overlay={<Tooltip>Supprimer</Tooltip>}>
                                                                    <button
                                                                        className="action-btn-charte"
                                                                        style={{ '--btn-color': COLORS.danger }}
                                                                        onClick={() => handleDeleteClick(operation)}
                                                                    >
                                                                        <i className="ri-delete-bin-line"></i>
                                                                    </button>
                                                                </OverlayTrigger>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    {isExpanded && paiementsAvecCalcul.length > 0 && (
                                                        <tr className="sub-row-charte">
                                                            <td colSpan="8" className="p-0">
                                                                <div className="paiement-sub-table-charte">
                                                                    <div className="d-flex align-items-center gap-2 mb-2">
                                                                        <i className="ri-history-line" style={{ color: COLORS.primary }}></i>
                                                                        <strong className="small">Historique des paiements</strong>
                                                                        <span className="badge-modern" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                                                            {paiementsAvecCalcul.length} opération(s)
                                                                        </span>
                                                                    </div>
                                                                    <Table size="sm" className="mb-0 table-charte">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Bordereau</th>
                                                                                <th>Date</th>
                                                                                <th className="text-end">Montant dû avant</th>
                                                                                <th className="text-end">Montant payé</th>
                                                                                <th className="text-end">Reste après</th>
                                                                                <th className="text-center">Signature</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {paiementsAvecCalcul.map((p, idx) => (
                                                                                <tr key={idx}>
                                                                                    <td>
                                                                                        <span className="reference-badge-charte">
                                                                                            <i className="ri-barcode-line"></i>{p.numero_bordereau}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td>
                                                                                        <div className="small">{formatDate(p.created_at)}</div>
                                                                                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                                                            {new Date(p.created_at).toLocaleTimeString('fr-FR')}
                                                                                        </small>
                                                                                    </td>
                                                                                    <td className="text-end fw-bold">{formatCurrency(p.montant_du_avant)}</td>
                                                                                    <td className="text-end fw-semibold" style={{ color: COLORS.success }}>
                                                                                        {formatCurrency(p.montant_paye)}
                                                                                    </td>
                                                                                    <td className="text-end">
                                                                                        {p.reste_apres <= 0 ? (
                                                                                            <span className="badge-solde-charte">
                                                                                                <i className="ri-check-line"></i>Soldé
                                                                                            </span>
                                                                                        ) : (
                                                                                            <span className="badge-reste-charte">
                                                                                                {formatCurrency(p.reste_apres)}
                                                                                            </span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td className="text-center">
                                                                                        {p.signature ? (
                                                                                            <i className="ri-checkbox-circle-fill" style={{ color: COLORS.success, fontSize: '1.1rem' }} title="Signé"></i>
                                                                                        ) : (
                                                                                            <i className="ri-close-circle-line text-muted" title="Non signé"></i>
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                        <tfoot>
                                                                            <tr>
                                                                                <td colSpan="2" className="fw-semibold">Récapitulatif</td>
                                                                                <td className="text-end fw-bold">{formatCurrency(operation.montantTotal)}</td>
                                                                                <td className="text-end fw-bold" style={{ color: COLORS.success }}>
                                                                                    {formatCurrency(operation.totalVersé)}
                                                                                </td>
                                                                                <td className="text-end fw-bold" style={{ color: COLORS.danger }}>
                                                                                    {formatCurrency(operation.resteReel)}
                                                                                </td>
                                                                                <td></td>
                                                                            </tr>
                                                                        </tfoot>
                                                                    </Table>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan="8">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte">
                                                            <i className="ri-inbox-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">Aucune opération trouvée</h6>
                                                        <p className="text-muted small mb-0">Essayez de modifier vos critères de recherche</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== PAGINATION ===== */}
                    {totalPages > 1 && !loading && (
                        <div className="pagination-wrapper-charte mt-3">
                            <div className="pagination-info-charte">
                                Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong> décaissements
                            </div>
                            <TablePagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    )}
                </div>
                <Footer />
            </div>

            {/* ===== MODAL SUPPRESSION ===== */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-delete-bin-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Confirmer la suppression</Modal.Title>
                            <p className="text-white-50 mb-0 small">Cette action est irréversible</p>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body className="p-4">
                    {operationToDelete && (
                        <>
                            <p className="text-center text-muted mb-3">
                                Êtes-vous sûr de vouloir supprimer définitivement ce décaissement ?
                            </p>

                            <div className="summary-card-charte mb-3">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="beneficiaire-avatar-charte">
                                        {operationToDelete.beneficiaire_prenom?.charAt(0)}{operationToDelete.beneficiaire_nom?.charAt(0)}
                                    </div>
                                    <div>
                                        <div className="fw-bold" style={{ color: COLORS.primaryDark }}>
                                            {operationToDelete.beneficiaire_prenom} {operationToDelete.beneficiaire_nom}
                                        </div>
                                        <div className="text-muted small">
                                            <i className="ri-barcode-line me-1"></i>
                                            Bordereau: {operationToDelete.numero_bordereau}
                                        </div>
                                        <div className="text-muted small">
                                            <i className="ri-money-euro-circle-line me-1"></i>
                                            Montant: {formatCurrency(operationToDelete.montantTotal || 0)}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="alert-error-charte mb-0">
                                <div className="d-flex align-items-center">
                                    <div className="alert-error-icon"><i className="ri-alert-line"></i></div>
                                    <div className="ms-3">
                                        <strong>Attention</strong>
                                        <p className="mb-0 small">Toutes les données associées à ce décaissement seront définitivement supprimées</p>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowDeleteModal(false)} className="btn-modern-outline" disabled={deleting}>
                        Annuler
                    </Button>
                    <Button onClick={handleConfirmDelete} disabled={deleting} className="btn-modern-danger">
                        {deleting ? (
                            <><Spinner size="sm" className="me-2" />Suppression...</>
                        ) : (
                            <><i className="ri-delete-bin-line me-2"></i>Oui, supprimer</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .search-box-charte { position: relative; }
                .search-icon-charte {
                    position: absolute; top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--primary); font-size: 1rem;
                    z-index: 2; pointer-events: none;
                }
                .search-input-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem 0.6rem 2.75rem !important;
                    font-size: 0.9rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .search-input-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .filter-select-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.9rem;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .filter-select-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .btn-modern-light-outline {
                    background: rgba(255,255,255,0.15);
                    border: 1.5px solid rgba(255,255,255,0.3);
                    color: white;
                    border-radius: 50px;
                    padding: 0.5rem 1rem;
                    font-size: 0.85rem;
                    font-weight: 500;
                    transition: all 0.2s ease;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .btn-modern-light-outline:hover {
                    background: rgba(255,255,255,0.25);
                    color: white;
                }
                .count-badge-charte {
                    background: rgba(255,255,255,0.95); color: var(--primary);
                    padding: 0.6rem 1.25rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }
                .btn-header-light {
                    background: rgba(255,255,255,0.2);
                    border: 1px solid rgba(255,255,255,0.3);
                    color: white; border-radius: 50px;
                    padding: 0.35rem 0.9rem;
                    font-size: 0.75rem; font-weight: 500;
                    transition: all 0.2s ease;
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                }
                .btn-header-light:hover {
                    background: rgba(255,255,255,0.3);
                    color: white;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-charte tbody tr.expanded-row-charte { background: #f8fafc; }
                .sortable-th-charte { cursor: pointer; user-select: none; transition: color 0.2s; }
                .sortable-th-charte:hover { color: var(--primary); }

                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                    transition: all 0.2s ease;
                }
                .reference-badge-charte.clickable { cursor: pointer; }
                .reference-badge-charte.clickable:hover {
                    background: ${COLORS.primary}15;
                    color: var(--primary);
                }
                .expand-icon {
                    transition: transform 0.2s ease;
                }
                tr.expanded-row-charte .expand-icon {
                    transform: rotate(90deg);
                }

                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    padding: 0.3rem 0.75rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 700;
                }
                .status-solde {
                    background: ${COLORS.success}15; color: var(--success);
                }
                .status-reste {
                    background: ${COLORS.danger}15; color: var(--danger);
                }
                .badge-solde-charte {
                    display: inline-flex; align-items: center; gap: 3px;
                    background: ${COLORS.success}15; color: var(--success);
                    padding: 0.2rem 0.6rem; border-radius: 50px;
                    font-size: 0.7rem; font-weight: 700;
                }
                .badge-reste-charte {
                    display: inline-flex; align-items: center;
                    background: ${COLORS.danger}15; color: var(--danger);
                    padding: 0.2rem 0.6rem; border-radius: 50px;
                    font-size: 0.7rem; font-weight: 700;
                }

                /* ===== SUB TABLE (paiements) ===== */
                .sub-row-charte td { padding: 0 !important; }
                .paiement-sub-table-charte {
                    padding: 1rem 1.5rem 1rem 4rem;
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    border-top: 1px solid #f1f5f9;
                }
                .paiement-sub-table-charte .table-charte thead th {
                    background: white; font-size: 0.65rem;
                    padding: 0.6rem 0.5rem;
                }
                .paiement-sub-table-charte .table-charte tbody td {
                    padding: 0.6rem 0.5rem;
                    font-size: 0.8rem;
                }
                .paiement-sub-table-charte .table-charte tfoot td {
                    background: #f8fafc;
                    padding: 0.7rem 0.5rem;
                    font-size: 0.8rem;
                    border-top: 2px solid #e2e8f0;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 0.95rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 1rem 1.5rem;
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    flex-wrap: wrap; gap: 1rem;
                }
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link {
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 0.9rem;
                    font-weight: 500; font-size: 0.85rem;
                    transition: all 0.2s;
                }
                .pagination-charte .page-item.active .page-link {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border-color: var(--primary);
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .pagination-charte .page-item .page-link:hover {
                    background: #f1f5f9;
                    border-color: var(--secondary);
                    color: var(--primary);
                    transform: translateY(-1px);
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }
                .summary-card-charte {
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    padding: 1rem 1.25rem;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }
                .beneficiaire-avatar-charte {
                    width: 52px; height: 52px; border-radius: 12px;
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-weight: 700; font-size: 1.15rem;
                    flex-shrink: 0;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.25);
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-danger {
                    background: linear-gradient(135deg, var(--danger), #dc2626);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(239,68,68,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-danger:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(239,68,68,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled,
                .btn-modern-danger:disabled { opacity: 0.6; transform: none; }

                /* ===== ALERTES ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                    .paiement-sub-table-charte { padding: 0.75rem 0.75rem 0.75rem 1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}